import React, { useState, useRef, useEffect } from 'react';

/**
 * MixerControls
 * Lets user preview and adjust backing track volume, vocal volume,
 * and sync offset before the final mix is created and uploaded.
 */
export default function MixerControls({
  backingTrackUrl,
  vocalBlob,
  backingVolume,
  vocalVolume,
  syncOffset,
  onBackingVolumeChange,
  onVocalVolumeChange,
  onSyncOffsetChange,
  isMixing,
  mixProgress,
}) {
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const previewCtxRef = useRef(null);
  const backingSourceRef = useRef(null);
  const vocalSourceRef = useRef(null);
  const backingGainRef = useRef(null);
  const vocalGainRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(0);

  // Update gain nodes in real-time as sliders change
  useEffect(() => {
    if (backingGainRef.current) backingGainRef.current.gain.value = backingVolume;
  }, [backingVolume]);

  useEffect(() => {
    if (vocalGainRef.current) vocalGainRef.current.gain.value = vocalVolume;
  }, [vocalVolume]);

  const stopPreview = () => {
    try { backingSourceRef.current?.stop(); } catch(e) {}
    try { vocalSourceRef.current?.stop(); } catch(e) {}
    backingSourceRef.current = null;
    vocalSourceRef.current = null;
    cancelAnimationFrame(animRef.current);
    previewCtxRef.current?.close();
    previewCtxRef.current = null;
    setIsPreviewPlaying(false);
    setPreviewTime(0);
  };

  const startPreview = async () => {
    if (isPreviewPlaying) { stopPreview(); return; }

    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      previewCtxRef.current = ctx;

      const [backingArray, vocalArray] = await Promise.all([
        fetch(backingTrackUrl).then(r => r.arrayBuffer()),
        vocalBlob.arrayBuffer(),
      ]);

      const [backingBuffer, vocalBuffer] = await Promise.all([
        ctx.decodeAudioData(backingArray),
        ctx.decodeAudioData(vocalArray),
      ]);

      // Backing
      const backingSource = ctx.createBufferSource();
      backingSource.buffer = backingBuffer;
      const backingGain = ctx.createGain();
      backingGain.gain.value = backingVolume;
      backingSource.connect(backingGain);
      backingGain.connect(ctx.destination);
      backingGainRef.current = backingGain;
      backingSourceRef.current = backingSource;

      // Vocal
      const vocalSource = ctx.createBufferSource();
      vocalSource.buffer = vocalBuffer;
      const vocalGain = ctx.createGain();
      vocalGain.gain.value = vocalVolume;
      vocalSource.connect(vocalGain);
      vocalGain.connect(ctx.destination);
      vocalGainRef.current = vocalGain;
      vocalSourceRef.current = vocalSource;

      // Apply sync offset
      const vocalStartTime = Math.max(0, syncOffset);
      const backingStartOffset = Math.max(0, -syncOffset);

      backingSource.start(0, backingStartOffset);
      vocalSource.start(vocalStartTime);

      startTimeRef.current = ctx.currentTime;
      setIsPreviewPlaying(true);

      // Track time
      const tick = () => {
        if (previewCtxRef.current) {
          setPreviewTime(previewCtxRef.current.currentTime - startTimeRef.current);
          animRef.current = requestAnimationFrame(tick);
        }
      };
      animRef.current = requestAnimationFrame(tick);

      backingSource.onended = () => stopPreview();
    } catch (err) {
      console.error('Preview failed:', err);
      stopPreview();
    }
  };

  useEffect(() => () => stopPreview(), []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  return (
    <div className="mixer-controls">
      <h3 className="mixer-title">🎚️ Mix Your Track</h3>
      <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
        Adjust volumes and timing, then preview before saving.
      </p>

      {/* Volume sliders */}
      <div className="mixer-row">
        <div className="mixer-label">
          <span>🎵 Backing Track</span>
          <span className="mixer-value">{Math.round(backingVolume * 100)}%</span>
        </div>
        <input
          type="range" min={0} max={1.5} step={0.01}
          value={backingVolume}
          onChange={e => onBackingVolumeChange(parseFloat(e.target.value))}
          className="mixer-slider backing-slider"
          disabled={isMixing}
        />
      </div>

      <div className="mixer-row">
        <div className="mixer-label">
          <span>🎙️ Your Vocals</span>
          <span className="mixer-value">{Math.round(vocalVolume * 100)}%</span>
        </div>
        <input
          type="range" min={0} max={2} step={0.01}
          value={vocalVolume}
          onChange={e => onVocalVolumeChange(parseFloat(e.target.value))}
          className="mixer-slider vocal-slider"
          disabled={isMixing}
        />
      </div>

      {/* Sync offset */}
      <div className="mixer-row">
        <div className="mixer-label">
          <span>⚡ Sync Offset</span>
          <span className="mixer-value">
            {syncOffset > 0 ? '+' : ''}{syncOffset.toFixed(2)}s
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="text-xs text-muted">-2s</span>
          <input
            type="range" min={-2} max={2} step={0.05}
            value={syncOffset}
            onChange={e => onSyncOffsetChange(parseFloat(e.target.value))}
            className="mixer-slider"
            style={{ flex: 1 }}
            disabled={isMixing}
          />
          <span className="text-xs text-muted">+2s</span>
        </div>
        <p className="text-xs text-muted" style={{ marginTop: 4 }}>
          {syncOffset < 0 ? 'Vocals start earlier' : syncOffset > 0 ? 'Vocals start later' : 'Vocals in sync'}
        </p>
      </div>

      {/* Preview button */}
      <button
        className={`btn ${isPreviewPlaying ? 'btn-warning' : 'btn-secondary'} mixer-preview-btn`}
        onClick={startPreview}
        disabled={isMixing}
      >
        {isPreviewPlaying ? (
          <><span>⏹</span> Stop Preview ({formatTime(previewTime)})</>
        ) : (
          <><span>▶️</span> Preview Mix</>
        )}
      </button>

      {/* Mixing progress */}
      {isMixing && (
        <div className="mix-progress">
          <div className="mix-progress-bar" style={{ width: `${mixProgress}%` }} />
          <span className="mix-progress-label">
            🎛️ Mixing... {mixProgress}%
          </span>
        </div>
      )}
    </div>
  );
}
